import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { RoleIdSchema, StaffOfficeSchema, STAFF_OFFICES, StaffEmailSchema, type RoleId } from '@shared';
import { apiPost, ApiError } from '../../lib/api';
import { applyFieldErrors } from '../../lib/form-errors';

// Client-side resolver — mirrors the server's InviteInput (routes/users.ts).
// The server stays the canonical validator; this just gives instant feedback.
const InviteStaffResolverSchema = z.object({
  name: z.string().min(1, 'Full name is required').max(100),
  email: StaffEmailSchema,
  role: RoleIdSchema,
  office: StaffOfficeSchema,
});
type InviteStaffInput = z.infer<typeof InviteStaffResolverSchema>;

export interface InvitedUser {
  id: string;
  email: string;
  name: string;
  role: RoleId;
  office?: string;
  status?: 'invited' | 'active' | 'suspended' | 'deactivated';
  initials?: string;
}

interface InviteUserModalProps {
  // Live role list (id + label) from GET /api/users/roles — drives the dropdown.
  roles: Array<{ id: string; label: string }>;
  onClose: () => void;
  onInvited: (user: InvitedUser) => void;
}

export function InviteUserModal({ roles, onClose, onInvited }: InviteUserModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const firstFocusable = useRef<HTMLButtonElement>(null);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<InviteStaffInput>({
    resolver: zodResolver(InviteStaffResolverSchema),
    defaultValues: {
      name: '',
      email: '',
      role: 'analyst',
      office: 'London',
    },
  });

  // Escape closes + body-scroll-lock — same modal hygiene as InviteContactModal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstFocusable.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (autoCloseTimer.current) {
        clearTimeout(autoCloseTimer.current);
        autoCloseTimer.current = null;
      }
    };
  }, [onClose, isSubmitting]);

  const onSubmit = async (data: InviteStaffInput) => {
    if (justSaved) return;
    setSubmitError(null);
    try {
      const r = await apiPost<{ user: InvitedUser }>('/api/users', data);
      onInvited(r.user);
      setJustSaved(true);
      autoCloseTimer.current = setTimeout(() => {
        autoCloseTimer.current = null;
        onClose();
      }, 1200);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: { code?: string; detail?: unknown } } | null;
        const code = body?.error?.code;
        if (err.status === 400 && code === 'validation_failed') {
          const handled = applyFieldErrors<InviteStaffInput>(body?.error?.detail, setError);
          if (!handled) setSubmitError(err.serverMessage);
          return;
        }
        if (err.status === 409 && code === 'email_in_use') {
          setError('email', {
            type: 'server',
            message: 'A user with this email already exists',
          });
          return;
        }
        setSubmitError(err.serverMessage);
      } else {
        setSubmitError('Failed to add the user. Please try again.');
      }
    }
  };

  return (
    <>
      <div
        className="drawer-backdrop"
        onClick={() => !isSubmitting && onClose()}
        aria-hidden="true"
      />
      <div
        className="invite-contact-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-user-h"
      >
        <header className="invite-contact-hd">
          <div>
            <h2 id="invite-user-h" className="drawer-title">Add staff user</h2>
            <div className="drawer-meta">
              <span>New member of the Omega staff roster</span>
            </div>
          </div>
          <button
            ref={firstFocusable}
            type="button"
            className="drawer-close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            ×
          </button>
        </header>

        <form className="invite-contact-body" onSubmit={handleSubmit(onSubmit)} noValidate>
          <p className="client-create-help" style={{ margin: 0 }}>
            The account is created in <strong>invited</strong> status — adjust their role or
            activate them from the People list.
          </p>

          <div className="field">
            <label htmlFor="invite-user-name">Full name<span className="req">*</span></label>
            <input
              id="invite-user-name"
              type="text"
              autoComplete="name"
              disabled={isSubmitting}
              aria-invalid={errors.name ? 'true' : 'false'}
              {...register('name')}
            />
            {errors.name && <div className="client-create-err">{errors.name.message}</div>}
          </div>

          <div className="field">
            <label htmlFor="invite-user-email">Corporate email<span className="req">*</span></label>
            <input
              id="invite-user-email"
              type="email"
              autoComplete="email"
              placeholder="name@example.com"
              disabled={isSubmitting}
              aria-invalid={errors.email ? 'true' : 'false'}
              {...register('email')}
            />
            {errors.email && <div className="client-create-err">{errors.email.message}</div>}
          </div>

          <div className="field">
            <label htmlFor="invite-user-role">Role<span className="req">*</span></label>
            <select
              id="invite-user-role"
              disabled={isSubmitting}
              {...register('role')}
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
            {errors.role && <div className="client-create-err">{errors.role.message}</div>}
          </div>

          <div className="field">
            <label htmlFor="invite-user-office">Office<span className="req">*</span></label>
            <select
              id="invite-user-office"
              disabled={isSubmitting}
              {...register('office')}
            >
              {STAFF_OFFICES.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            {errors.office && <div className="client-create-err">{errors.office.message}</div>}
          </div>

          {submitError && (
            <div className="client-create-banner" role="alert">
              {submitError}
            </div>
          )}
          {justSaved && (
            <div
              role="status"
              aria-live="polite"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 14px',
                borderRadius: 'var(--r-sm)',
                background: 'color-mix(in oklab, var(--green, #1f8a4c) 12%, transparent)',
                color: 'var(--green, #1f8a4c)',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <span aria-hidden="true">✓</span>
              <span>User added.</span>
            </div>
          )}

          <div className="invite-contact-actions">
            <button
              type="button"
              className="btn ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {justSaved ? 'Close now' : 'Cancel'}
            </button>
            <button type="submit" className="btn accent" disabled={isSubmitting || justSaved}>
              {isSubmitting ? 'Adding…' : justSaved ? 'Added ✓' : 'Add user'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
